/**
 * index.tsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  InMemoryCache
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { Context } from '@growthbook/growthbook-react';
import { GrowthbookClientProvider } from '@openfeature/growthbook-client-provider';
import { OpenFeature, OpenFeatureProvider } from '@openfeature/react-sdk';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Import root app
import { App } from 'app';
import axios, { AxiosError } from 'axios';
import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill';
import { isEqual, uniqWith } from 'lodash';
import { StrictMode } from 'react';
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import 'sanitize.css/sanitize.css';

import 'app/components/QuillFormats/Link';
import { NuqsAdapter } from 'nuqs/adapters/react-router';
import { configureAppStore } from 'store/configureStore';
import 'utils/sentry';

// Initialize languages
import './locales/i18n';

// Initialize Apollo
const gatewayLink = createHttpLink({
  uri: import.meta.env.VITE_GQL_URL
});

const federationLink = createHttpLink({
  uri: import.meta.env.VITE_FEDERATION_URL
});

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('jwt-token');
  const refreshToken = localStorage.getItem('refresh-token');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      refresh: refreshToken || ''
    }
  };
});

const refreshLink = new ApolloLink((op, forward) => {
  return forward(op).map(r => {
    const context = op.getContext();

    const {
      response: { headers }
    } = context;
    if (headers) {
      const accessToken = headers.get('access-refresh');
      if (accessToken) {
        localStorage.setItem('jwt-token', accessToken);
      }
    }
    return r;
  });
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (import.meta.env.VITE_NODE_ENV !== 'production' || import.meta.env.VITE_IS_STAGING) {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) =>
        console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      );
    }

    if (networkError) {
      console.error(`[Network error]: ${networkError}`);
    }
  }
});

const client = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      Faculty: {
        fields: {
          courses: {
            merge(existing = [], incoming) {
              return uniqWith([...existing, ...incoming], isEqual);
            }
          }
        }
      },
      Query: {
        fields: {
          appointments: {
            merge(existing = [], incoming) {
              return uniqWith([...existing, ...incoming], isEqual);
            }
          }
        }
      }
    }
  }),
  // @ts-ignore There seems to be a ts types mismatch
  link: errorLink.concat(
    refreshLink.concat(
      //@ts-ignore There seems to be a ts types mismatch
      authLink.concat(
        //@ts-ignore There seems to be a ts types mismatch
        ApolloLink.split(
          operation => operation.getContext().clientName === 'federation',
          federationLink,
          gatewayLink
        )
      )
    )
  )
});

const MAX_RETRIES = 6;
const HTTP_STATUS_TO_NOT_RETRY = [400, 401, 403, 404];

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (failureCount > MAX_RETRIES) {
          return false;
        }

        if (
          error instanceof AxiosError &&
          HTTP_STATUS_TO_NOT_RETRY.includes(error.response?.status ?? 0)
        ) {
          return false;
        }

        return true;
      }
    }
  }
});

const token = localStorage.getItem('jwt-token');
const refreshToken = localStorage.getItem('refresh-token');

export const Gateway = axios.create({
  baseURL: import.meta.env.VITE_GATEWAY_URL,
  headers: { authorization: token ? `Bearer ${token}` : '', refresh: refreshToken || '' }
});

const store = configureAppStore();

// ? This is a workaround for the issue with the country flag emojis not being displayed correctly some browsers on windows.
polyfillCountryFlagEmojis();

const gbContext: Context = {
  apiHost: 'https://api.features.artcodix.com',
  clientKey: import.meta.env.VITE_GROWTHBOOK_KEY
};

OpenFeature.setProvider(new GrowthbookClientProvider(gbContext));

interface Props {
  Component: typeof App;
}

const ConnectedApp = ({ Component }: Props) => (
  <OpenFeatureProvider>
    <ApolloProvider client={client}>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <HelmetProvider>
            <StrictMode>
              <BrowserRouter>
                <NuqsAdapter>
                  <Component />
                </NuqsAdapter>
              </BrowserRouter>
            </StrictMode>
          </HelmetProvider>
        </QueryClientProvider>
      </Provider>
    </ApolloProvider>
  </OpenFeatureProvider>
);

let container: HTMLElement;

document.addEventListener('DOMContentLoaded', function (event) {
  if (!container) {
    container = document.getElementById('root') as HTMLElement;
    render(App);
  }
});

const render = (Component: typeof App) => {
  createRoot(container).render(<ConnectedApp Component={Component} />);
};
